import {
  sendErrorNotification,
  sendSuccessNotification
} from '@dtx-company/inter-app/src/utils/notifications'
import {
  useAuthorizeSalesforceMutation,
  useDeleteSalesforceMutation
} from '@dtx-company/inter-app/src/redux/slices/flowpageApiSlice/crm'
import { useRouter } from 'next/router'
import { v4 as uuid } from 'uuid'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/crm'

export type CRMTypeStateId = 'salesforce' | 'gsheets'

export const localPrefix = 'crm-integration_'
//use the local prefix so that we can use this util for multiple crm
const getStateIdFromLocalStorage = (crmType: CRMTypeStateId): string | null => {
  return global?.window?.localStorage?.getItem(`${localPrefix}${crmType}`)
}

export const verifyStateId = ({
  queryState,
  crmType
}: {
  queryState?: string | null | string[]
  crmType: CRMTypeStateId
}): boolean => {
  const localStorageStateId = getStateIdFromLocalStorage(crmType)
  let queryStateId = ''
  if (typeof queryState === 'string') {
    queryStateId = queryState.includes(crmType) ? queryState?.replace(crmType + '-', '') : ''
  }

  return queryStateId === localStorageStateId
}
export const storeStateIdInLocalStorage = ({
  crmType,
  stateId
}: {
  crmType: CRMTypeStateId
  stateId: string
}): void => {
  window.localStorage.setItem(`${localPrefix}${crmType}`, stateId)
}

export function useOnClickSalesforce(): ({
  domain,
  setError
}: {
  domain: string
  setError(_0: string): void
}) => Promise<void> {
  const stateId = React.useMemo(() => uuid(), [])
  const [authorizeSalesForceMutation] = useAuthorizeSalesforceMutation()

  const router = useRouter()

  return async ({ domain, setError }) => {
    events.Flowpage_CRM_Clicked_AuthorizeSalesforce()

    if (!domain) {
      setError('A domain is required')
      return
    }
    try {
      const resp = await authorizeSalesForceMutation({
        domain
      })
      if ('error' in resp) {
        sendErrorNotification('Error authorizing salesforce')
        console.error('Error authorizing salesforce domain', resp.error.message)
      }
      if ('data' in resp && resp.data?.authorizeSalesforceIntegration) {
        storeStateIdInLocalStorage({ crmType: 'salesforce', stateId })
        router.push(
          `${resp.data?.authorizeSalesforceIntegration}${encodeURI(`&state=salesforce-${stateId}`)}`
        )
      }
    } catch (e) {
      sendErrorNotification('Error authorizing salesforce')
      console.error('Error authorizing salesforce domain', e.message)
    }
  }
}

export function useOnDeleteSalesforce(): (hideMessaging?: boolean) => Promise<void> {
  const [deleteSalesforceMutation] = useDeleteSalesforceMutation()
  return async (hideMessaging = false) => {
    try {
      events.Flowpage_CRM_Clicked_DeleteSalesforce()
      await deleteSalesforceMutation({})
      !hideMessaging && sendSuccessNotification('Deleted Salesforce Domain Integration')
    } catch (e) {
      !hideMessaging && sendErrorNotification('Error deleting salesforce')
      console.error('Error deleting salesforce domain', e.message)
    }
  }
}
